import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawerV1 extends Component {
    requiredRefs = ['details'];

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener('keyup', this.#onKeyUp);
        this.#setupAnimatedElementListeners();
        this.#bindSummaryEvents();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('keyup', this.#onKeyUp);
    }

    /**
     * Close the main menu drawer or current submenu when the Escape key is pressed.
     * If a submenu is open, closes that submenu first. If the main drawer is open, closes it.
     * @param {KeyboardEvent} event - The keyboard event
     * @returns {void}
     */
    #onKeyUp = (event) => {
        if (event.key !== 'Escape') return;

        const target = event.target;
        if (!(target instanceof Element)) return;

        const openDetailsElement = target.closest('details[open]');
        if (!openDetailsElement) return;

        if (openDetailsElement === this.refs.details) {
            this.#close(this.refs.details);
        } else if (openDetailsElement instanceof HTMLDetailsElement) {
            this.#closeSubmenu(openDetailsElement);
        }
    };

    /**
     * @returns {boolean} Whether the main menu drawer is open
     */
    get isOpen() {
        return this.refs.details.hasAttribute('open');
    }

    /**
     * Get the closest details element to the event target
     * @param {Event | undefined} event
     * @returns {HTMLDetailsElement}
     */
    #getDetailsElement(event) {
        if (!(event?.target instanceof Element)) return this.refs.details;

        return event.target.closest('details') ?? this.refs.details;
    }

    /**
     * Toggle the main menu drawer
     */
    toggle() {
        return this.isOpen ? this.close() : this.open();
    }

    /**
     * Open the closest drawer or the main menu drawer
     * @param {Event} [event]
     */
    open(event) {
        const details = this.#getDetailsElement(event);
        const summary = details.querySelector('summary');

        if (!summary) return;

        summary.setAttribute('aria-expanded', 'true');

        this.preventInitialAccordionAnimations(details);
        requestAnimationFrame(() => {
            details.classList.add('menu-open');
            setTimeout(() => {
                trapFocus(details);
            }, 0);
        });
    }

    /**
     * Go back or close the main menu drawer
     * @param {Event} [event]
     */
    back(event) {
        this.#close(this.#getDetailsElement(event));
    }

    /**
     * Close the main menu drawer
     */
    close() {
        this.#close(this.refs.details);
    }

    /**
     * Close the closest menu or submenu that is open
     *
     * @param {HTMLDetailsElement} details
     */
    #close(details) {
        const summary = details.querySelector('summary');

        if (!summary) return;

        summary.setAttribute('aria-expanded', 'false');
        details.classList.remove('menu-open');

        onAnimationEnd(details, () => {
            reset(details);
            if (details === this.refs.details) {
                removeTrapFocus();
                const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
                openDetails.forEach(reset);
            } else {
                setTimeout(() => {
                    trapFocus(this.refs.details);
                }, 0);
            }
        });
    }

    /**
     * Attach animationend event listeners to all animated elements to remove will-change after animation
     * to remove the stacking context and allow submenus to be positioned correctly
     */
    #setupAnimatedElementListeners() {
        /**
         * @param {AnimationEvent} event
         */
        function removeWillChangeOnAnimationEnd(event) {
            const target = event.target;
            if (target && target instanceof HTMLElement) {
                target.style.setProperty('will-change', 'unset');
                target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
            }
        }
        const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
        allAnimated.forEach((element) => {
            element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
        });
    }

    /**
     * Binds click event listeners to all summary elements within the drawer.
     * Enables submenu opening and focus management when summaries are clicked.
     * @returns {void}
     */
    #bindSummaryEvents() {
        const summaries = this.querySelectorAll('summary');
        summaries.forEach((summary) => summary.addEventListener('click', this.#onSummaryClick));
    }

    /**
     * Binds click event listeners to all close/back buttons within submenus.
     * Enables submenu closing when the back button is clicked.
     * @returns {void}
     * @param {Event | undefined} event
     */
    backSubmenu(event) {
        const details = this.#getDetailsElement(event);
        if (details instanceof HTMLDetailsElement) this.#closeSubmenu(details);
    }

    /**
     * Handles click events on summary elements to open submenus.
     * Adds 'menu-opening' class, sets aria-expanded, updates parent menu state, and traps focus.
     * Respects prefers-reduced-motion for accessibility.
     * @param {MouseEvent} event - The click event from a summary element
     * @returns {void}
     */
    #onSummaryClick = (event) => {
        const currentTarget = event.currentTarget;
        if (!(currentTarget instanceof HTMLElement)) return;

        const summaryElement = currentTarget;
        const detailsElement = summaryElement.parentNode;
        if (!(detailsElement instanceof HTMLDetailsElement)) return;

        // If it's the main drawer summary, allow existing open/close flow
        if (detailsElement === this.refs.details) return;

        const parentMenuElement = detailsElement.closest('.has-submenu') || detailsElement.closest('.menu-drawer__menu');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        const addTrapFocus = () => {
            const nextSibling = summaryElement.nextElementSibling;
            if (!(nextSibling instanceof HTMLElement)) return;

            trapFocus(nextSibling);
            nextSibling.removeEventListener('transitionend', addTrapFocus);
        };

        // open submenu with slight delay to allow animation class to be read by CSS
        setTimeout(() => {
            detailsElement.classList.add('menu-opening');
            summaryElement.setAttribute('aria-expanded', 'true');
            parentMenuElement && parentMenuElement.classList.add('submenu-open');
            const nextSibling = summaryElement.nextElementSibling;
            reducedMotion.matches ? addTrapFocus() : nextSibling?.addEventListener('transitionend', addTrapFocus);
        }, 100);
    };

    /**
     * Closes a submenu and restores focus management.
     * Removes 'menu-opening' and 'submenu-open' classes, updates aria-expanded,
     * releases focus trap, and animates the close transition.
     * @param {HTMLDetailsElement} detailsElement - The details element representing the submenu to close
     * @returns {void}
     */
    #closeSubmenu(detailsElement) {
        if (!(detailsElement instanceof HTMLDetailsElement)) return;
        const parentMenuElement = detailsElement.closest('.submenu-open');
        parentMenuElement && parentMenuElement.classList.remove('submenu-open');
        detailsElement.classList.remove('menu-opening');
        const summary = detailsElement.querySelector('summary');
        if (summary) {
            summary.setAttribute('aria-expanded', 'false');
            removeTrapFocus();
        }
        this.#closeAnimation(detailsElement);
    }

    /**
     * Animates the closing of a submenu using requestAnimationFrame.
     * Waits for the close animation (400ms) to complete before removing the 'open' attribute.
     * Restores focus trap to the parent menu if one exists.
     * @param {HTMLDetailsElement} detailsElement - The details element to animate closing
     * @returns {void}
     */
    #closeAnimation(detailsElement) {
        let animationStart = 0;

        /** @param {DOMHighResTimeStamp} time */
        const handleAnimation = (time) => {
            if (animationStart === 0) animationStart = time;

            const elapsedTime = time - animationStart;

            if (elapsedTime < 400) {
                window.requestAnimationFrame(handleAnimation);
            } else {
                detailsElement.removeAttribute('open');
                const parentDetails = detailsElement.closest('details[open]');
                if (parentDetails instanceof HTMLDetailsElement) {
                    trapFocus(parentDetails);
                }
            }
        };

        window.requestAnimationFrame(handleAnimation);
    }

    /**
     * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
     * Adds a no-animation class to accordion content elements, then removes it after 100ms to
     * re-enable animations for user interactions.
     * @param {HTMLDetailsElement} details - The details element containing the accordions
     */
    preventInitialAccordionAnimations(details) {
        const content = details.querySelectorAll('accordion-custom .details-content');

        content.forEach((element) => {
            if (element instanceof HTMLElement) {
                element.classList.add('details-content--no-animation');
            }
        });
        setTimeout(() => {
            content.forEach((element) => {
                if (element instanceof HTMLElement) {
                    element.classList.remove('details-content--no-animation');
                }
            });
        }, 100);
    }
}

if (!customElements.get('header-drawer-v1')) {
    customElements.define('header-drawer-v1', HeaderDrawerV1);
}

/**
 * Reset an open details element to its original state.
 * Removes menu-open class, the open attribute, and sets aria-expanded to false.
 *
 * @param {HTMLDetailsElement} element - The details element to reset
 * @returns {void}
 */
function reset(element) {
    // Remove any menu state classes and attributes so the element
    // returns to a clean, closed state. Also clear submenu indicators
    // on parent menu containers to avoid stale classes after backdrop closes.
    element.classList.remove('menu-open', 'menu-opening');
    element.removeAttribute('open');

    const summary = element.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');

    const parentSubmenu = element.closest('.submenu-open');
    if (parentSubmenu instanceof HTMLElement) parentSubmenu.classList.remove('submenu-open');
}
